/**
 * Overlay layers: the pins themselves, and the toggleable traffic layers.
 *
 * Kept out of React. MapLibre owns this state, and mirroring it into component
 * state produces two sources of truth that disagree during a style reload.
 * Components call these functions; the map holds the result.
 */
import type { ExpressionSpecification, GeoJSONSource, Map as MapLibreMap } from 'maplibre-gl';
import type { Category, Pin } from '@/lib/api';

export const PIN_SOURCE = 'pins';
export const PIN_CLUSTERS = 'pin-clusters';
export const PIN_CLUSTER_COUNT = 'pin-cluster-count';
export const PIN_POINTS = 'pin-points';
export const PIN_LABELS = 'pin-labels';

/** Fallback for a pin whose category was deactivated after it was approved. */
const NEUTRAL = '#64748b';

export function pinsToGeoJSON(pins: Pin[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: pins.map((pin) => ({
      type: 'Feature',
      id: pin.id,
      geometry: { type: 'Point', coordinates: [pin.lon, pin.lat] },
      properties: {
        id: pin.id,
        title: pin.title,
        // Only the primary category reaches the style: a pin is drawn as one
        // circle and has to be one colour. The full list lives in the
        // snapshot and is shown in the detail card.
        category: pin.categories[0] ?? '',
        votes: pin.votes,
      },
    })),
  };
}

/**
 * `match` on the category slug, built from the categories the API sent.
 * Categories are data, so the colour ramp is data too — adding a category in
 * the admin UI colours its pins without a deploy.
 */
function categoryColor(categories: Category[]): ExpressionSpecification {
  const pairs = categories.flatMap((c) => [c.slug, c.color]);
  // A match with no branches is not expressible, so fall back to a literal.
  if (pairs.length === 0) return NEUTRAL as unknown as ExpressionSpecification;
  // The spread makes the arity unknowable to TypeScript, which types `match`
  // with a fixed minimum shape. The expression is valid; the type is not
  // expressive enough to see it.
  return ['match', ['get', 'category'], ...pairs, NEUTRAL] as unknown as ExpressionSpecification;
}

export function addPinLayers(map: MapLibreMap, pins: Pin[], categories: Category[]): void {
  if (map.getSource(PIN_SOURCE)) return;

  map.addSource(PIN_SOURCE, {
    type: 'geojson',
    data: pinsToGeoJSON(pins),
    cluster: true,
    clusterRadius: 48,
    // Above this zoom every pin is individually visible and clickable.
    clusterMaxZoom: 14,
    // Cluster circles are sized by how much support they represent, not just
    // how many markers happen to overlap.
    clusterProperties: { votes: ['+', ['get', 'votes']] },
  });

  map.addLayer({
    id: PIN_CLUSTERS,
    type: 'circle',
    source: PIN_SOURCE,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#0b3d91',
      'circle-radius': ['step', ['get', 'point_count'], 16, 10, 22, 50, 30],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.92,
    },
  });

  map.addLayer({
    id: PIN_CLUSTER_COUNT,
    type: 'symbol',
    source: PIN_SOURCE,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-font': ['Noto Sans Medium'],
      'text-size': 13,
      'text-allow-overlap': true,
    },
    paint: { 'text-color': '#ffffff' },
  });

  map.addLayer({
    id: PIN_POINTS,
    type: 'circle',
    source: PIN_SOURCE,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': categoryColor(categories),
      // Radius grows with support, but slowly — a pin with 200 votes must not
      // swallow its neighbours.
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['get', 'votes'],
        0, 7,
        10, 9,
        50, 12,
        200, 16,
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
    },
  });

  map.addLayer({
    id: PIN_LABELS,
    type: 'symbol',
    source: PIN_SOURCE,
    filter: ['!', ['has', 'point_count']],
    minzoom: 15,
    layout: {
      'text-field': ['get', 'title'],
      'text-font': ['Noto Sans Regular'],
      'text-size': 12,
      'text-offset': [0, 1.4],
      'text-anchor': 'top',
      'text-max-width': 12,
      'text-optional': true,
    },
    paint: {
      'text-color': '#10151f',
      'text-halo-color': '#ffffff',
      'text-halo-width': 1.5,
    },
  });
}

export function updatePins(map: MapLibreMap, pins: Pin[], categories: Category[]): void {
  const source = map.getSource<GeoJSONSource>(PIN_SOURCE);
  if (source) {
    source.setData(pinsToGeoJSON(pins));
  }
  if (map.getLayer(PIN_POINTS)) {
    map.setPaintProperty(PIN_POINTS, 'circle-color', categoryColor(categories));
  }
}

/**
 * Category filter.
 *
 * Implemented by re-setting the source data, not by a layer filter. MapLibre
 * clusters everything in the source before any layer filter runs, so filtering
 * only the point layer would leave cluster bubbles counting pins the user has
 * hidden — a visibly wrong number. Re-setting the data makes the clusterer run
 * over the visible subset, so the counts stay true.
 */
export function applyCategoryFilter(
  map: MapLibreMap,
  pins: Pin[],
  visible: ReadonlySet<string> | null,
): void {
  const source = map.getSource<GeoJSONSource>(PIN_SOURCE);
  if (!source) return;
  // A pin is shown when ANY of its categories is selected — hiding a
  // multi-category pin unless every one of its categories is on would make
  // cross-cutting problems the hardest ones to find.
  const subset = visible
    ? pins.filter((p) => p.categories.some((slug) => visible.has(slug)))
    : pins;
  source.setData(pinsToGeoJSON(subset));
}

/* ---------------------------------------------------------------------- */
/* Toggleable traffic layers                                              */
/* ---------------------------------------------------------------------- */

const OVERLAY_PREFIX = 'overlay-';

/** Where overlays are inserted: under the pins, above the basemap labels. */
function insertBefore(map: MapLibreMap): string | undefined {
  return map.getLayer(PIN_CLUSTERS) ? PIN_CLUSTERS : undefined;
}

/**
 * Road network by TSK class.
 *
 * Deliberately NOT captioned as traffic intensity — the source carries road
 * classification, not measured volumes. See scripts/ingest/cars.ts.
 */
function addRoadNetwork(map: MapLibreMap, id: string, data: GeoJSON.FeatureCollection): void {
  map.addSource(id, { type: 'geojson', data });
  map.addLayer(
    {
      id,
      type: 'line',
      source: id,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': [
          'match',
          ['get', 'rank'],
          5, '#7f1d1d',
          4, '#b91c1c',
          3, '#ea580c',
          2, '#f59e0b',
          '#fbbf24',
        ],
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10, ['match', ['get', 'rank'], 5, 3, 4, 2.2, 1.4],
          16, ['match', ['get', 'rank'], 5, 10, 4, 7, 4],
        ],
        'line-opacity': 0.75,
      },
    },
    insertBefore(map),
  );
}

/**
 * Cycling counters as proportional markers, not a heatmap.
 *
 * Prague has a few dozen counters. A heatmap over that many fixed points
 * interpolates a density surface the data does not support — it would read as
 * "this is where Prague cycles" when the data says "these forty points were
 * measured". Circles sized by the measured value say the true thing, and the
 * count label makes each one readable rather than impressionistic.
 */
function addBikeCounters(map: MapLibreMap, id: string, data: GeoJSON.FeatureCollection): void {
  map.addSource(id, { type: 'geojson', data });

  map.addLayer(
    {
      id,
      type: 'circle',
      source: id,
      paint: {
        'circle-color': ['case', ['==', ['get', 'value'], null], '#94a3b8', '#0f766e'],
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['coalesce', ['get', 'value'], 0],
          0, 5,
          500, 10,
          2000, 16,
          6000, 24,
        ],
        'circle-opacity': 0.8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
      },
    },
    insertBefore(map),
  );

  map.addLayer(
    {
      id: `${id}-label`,
      type: 'symbol',
      source: id,
      minzoom: 12,
      layout: {
        'text-field': [
          'case',
          ['==', ['get', 'value'], null],
          'bez dat',
          ['to-string', ['get', 'value']],
        ],
        'text-font': ['Noto Sans Medium'],
        'text-size': 11,
        'text-allow-overlap': false,
      },
      paint: {
        'text-color': '#0f172a',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.5,
      },
    },
    insertBefore(map),
  );
}

export function addOverlay(
  map: MapLibreMap,
  key: string,
  data: GeoJSON.FeatureCollection,
): void {
  const id = OVERLAY_PREFIX + key;
  if (map.getSource(id)) return;

  if (key === 'cyklo-scitace') addBikeCounters(map, id, data);
  else if (key === 'mhd-linky') addTransitLines(map, id, data);
  else addRoadNetwork(map, id, data);
}

/**
 * PID lines, coloured by mode.
 *
 * Colour carries the mode, not the individual line: 249 routes cannot each
 * have a legible colour, and what a reader needs from this layer is "is this
 * street served by a tram or only by a bus", not "which of the 173 bus routes
 * is this".
 *
 * Widths are ordered by capacity rather than uniformly, so metro and tram read
 * as the spine of the network at a glance and buses as the mesh around it.
 * Night routes are dashed — they are a separate network that happens to share
 * the streets, and drawing them solid would double every corridor.
 *
 * There is deliberately no per-route number label. A `symbol-placement: line`
 * layer over these 249 routes loses every collision against the basemap's own
 * street names — at z14 a single label survived across the whole viewport —
 * and forcing it to draw with `text-allow-overlap` turns the city view into a
 * wall of digits. Doing it properly means route shields: a rounded background
 * sprite per mode, placed sparsely, which is its own piece of work rather than
 * two more style properties. Until then the line's identity comes from the
 * popup and from `odkaz`, the timetable link carried on every feature.
 */
function addTransitLines(map: MapLibreMap, id: string, data: GeoJSON.FeatureCollection): void {
  map.addSource(id, { type: 'geojson', data });

  const colour: ExpressionSpecification = [
    'match',
    ['get', 'mode'],
    'metro', '#1d4ed8',
    'tram', '#b91c1c',
    'vlak', '#0f766e',
    'trolejbus', '#7c3aed',
    'privoz', '#0891b2',
    '#64748b',
  ];

  map.addLayer(
    {
      id,
      type: 'line',
      source: id,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': colour,
        'line-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10, ['match', ['get', 'mode'], 'metro', 3.5, 'tram', 2.4, 'vlak', 2, 1.1],
          16, ['match', ['get', 'mode'], 'metro', 9, 'tram', 6, 'vlak', 5, 3],
        ],
        // Night routes dashed; everything else solid. A zero-length dash
        // array is not allowed, so the day case is an explicit [1, 0].
        'line-dasharray': ['case', ['==', ['get', 'noc'], 1], ['literal', [2, 1.5]], ['literal', [1, 0]]],
        'line-opacity': 0.8,
      },
    },
    insertBefore(map),
  );

}

export function removeOverlay(map: MapLibreMap, key: string): void {
  const id = OVERLAY_PREFIX + key;
  for (const layerId of [`${id}-label`, id]) {
    if (map.getLayer(layerId)) map.removeLayer(layerId);
  }
  if (map.getSource(id)) map.removeSource(id);
}
